'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import PetGallery from './PetGallery';
import PetBasicInfo from './PetBasicInfo';
import PetPersonality from './PetPersonality';
import CareRequirements from './CareRequirements';
import ShelterInfo from './ShelterInfo';
import AdoptionTimeline from './AdoptionTimeline';
import CompatibilityScore from './CompatibilityScore';
import ActionButtons from './ActionButtons';
import RelatedPets from './RelatedPets';
import { SlideUp, FadeIn } from '@/components/ui/motion';

interface MediaItem {
  id: number;
  type: 'image' | 'video';
  url: string;
  alt: string;
  thumbnail?: string;
}

interface Trait {
  name: string;
  level: number;
  icon: string;
}

interface CareItem {
  category: string;
  icon: string;
  level: 'Low' | 'Medium' | 'High';
  details: string;
}

interface TimelineStep {
  step: number;
  title: string;
  description: string;
  icon: string;
  completed: boolean;
}

interface CompatibilityFactor {
  name: string;
  score: number;
  icon: string;
}

interface RelatedPet {
  id: number | string;
  name: string;
  breed: string;
  age: string;
  image: string;
  alt: string;
  location: string;
}

interface PetData {
  id: string;
  name: string;
  breed: string;
  age: string;
  gender: string;
  size: string;
  color: string;
  adoptionStatus: 'available' | 'pending' | 'adopted';
  media: MediaItem[];
  personalityDescription: string;
  traits: Trait[];
  goodWith: string[];
  specialNeeds: string[];
  careItems: CareItem[];
  dietaryNeeds: string;
  exerciseNeeds: string;
  groomingNeeds: string;
  shelter: {
    name: string;
    logo: string;
    logoAlt: string;
    location: string;
    phone: string;
    email: string;
    website: string;
    verified: boolean;
    adoptionFee: string;
  };
  timeline: TimelineStep[];
  compatibility: {
    overallScore: number;
    factors: CompatibilityFactor[];
  };
  relatedPets: RelatedPet[];
}

interface PetProfileInteractiveProps {
  petData: PetData;
}

const PetProfileInteractive = ({ petData }: PetProfileInteractiveProps) => {
  const router = useRouter();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleScheduleVisit = () => {
    if (!isHydrated) return;
    setShowScheduleModal(true);
  };

  const handleStartAdoption = () => {
    if (!isHydrated) return;
    // Navigate to adoption application form with pet ID
    router.push(`/adoption-application?petId=${petData.id}`);
  };

  const handleShare = () => {
    if (!isHydrated) return;
    setShowShareModal(true);
  };

  const closeModals = () => {
    if (!isHydrated) return;
    setShowScheduleModal(false);
    setShowShareModal(false);
  };

  return (
    <>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <PetGallery media={petData.media} petName={petData.name} />

          <SlideUp delay={0.1}>
            <PetBasicInfo
              name={petData.name}
              breed={petData.breed}
              age={petData.age}
              gender={petData.gender}
              size={petData.size}
              color={petData.color}
              adoptionStatus={petData.adoptionStatus}
            />
          </SlideUp>

          <SlideUp delay={0.2}>
            <PetPersonality
              description={petData.personalityDescription}
              traits={petData.traits}
              goodWith={petData.goodWith}
              specialNeeds={petData.specialNeeds}
            />
          </SlideUp>

          <SlideUp delay={0.3}>
            <CareRequirements
              careItems={petData.careItems}
              dietaryNeeds={petData.dietaryNeeds}
              exerciseNeeds={petData.exerciseNeeds}
              groomingNeeds={petData.groomingNeeds}
            />
          </SlideUp>

          <SlideUp delay={0.4}>
            <AdoptionTimeline steps={petData.timeline} />
          </SlideUp>
        </div>

        <div className="space-y-6">
          <FadeIn delay={0.2}>
            <ActionButtons
              petName={petData.name}
              onScheduleVisit={handleScheduleVisit}
              onStartAdoption={handleStartAdoption}
              onShare={handleShare}
            />
          </FadeIn>

          <SlideUp delay={0.3}>
            <ShelterInfo
              name={petData.shelter.name}
              logo={petData.shelter.logo}
              logoAlt={petData.shelter.logoAlt}
              location={petData.shelter.location}
              phone={petData.shelter.phone}
              email={petData.shelter.email}
              website={petData.shelter.website}
              verified={petData.shelter.verified}
              adoptionFee={petData.shelter.adoptionFee}
            />
          </SlideUp>

          <SlideUp delay={0.4}>
            <CompatibilityScore
              overallScore={petData.compatibility.overallScore}
              factors={petData.compatibility.factors}
            />
          </SlideUp>
        </div>
      </div>

      <div className="mt-12">
        <RelatedPets pets={petData.relatedPets} />
      </div>

      {isHydrated && showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-2xl font-headline font-semibold text-foreground">
              Schedule a Visit
            </h3>
            <p className="text-base text-muted-foreground font-body">
              Contact the shelter directly to schedule a visit with {petData.name}. They will help you arrange a convenient time to meet.
            </p>
            <div className="flex gap-4">
              <button
                onClick={closeModals}
                className="flex-1 px-6 py-3 bg-muted text-foreground font-body font-medium rounded-xl hover:bg-muted/80 transition-colors"
              >
                Close
              </button>
              <a
                href={`tel:${petData.shelter.phone}`}
                className="flex-1 px-6 py-3 bg-primary text-primary-foreground font-body font-medium rounded-xl hover:shadow-elevated transition-all text-center"
              >
                Call Shelter
              </a>
            </div>
          </div>
        </div>
      )}

      {isHydrated && showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-2xl font-headline font-semibold text-foreground">
              Share {petData.name}&apos;s Profile
            </h3>
            <p className="text-base text-muted-foreground font-body">
              Help {petData.name} find a loving home by sharing this profile with friends and family.
            </p>
            <div className="space-y-2">
              <button className="w-full px-6 py-3 bg-primary/10 text-primary font-body font-medium rounded-xl hover:bg-primary/20 transition-colors text-left">
                Copy Link
              </button>
              <button className="w-full px-6 py-3 bg-primary/10 text-primary font-body font-medium rounded-xl hover:bg-primary/20 transition-colors text-left">
                Share on Facebook
              </button>
              <button className="w-full px-6 py-3 bg-primary/10 text-primary font-body font-medium rounded-xl hover:bg-primary/20 transition-colors text-left">
                Share on Twitter
              </button>
            </div>
            <button
              onClick={closeModals}
              className="w-full px-6 py-3 bg-muted text-foreground font-body font-medium rounded-xl hover:bg-muted/80 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PetProfileInteractive;